/**
 * QuoteComparison — Step 5 rich detail.
 *
 * Side-by-side card grid: one card per quote. Highlights cert match
 * pass/fail and lets the user pick a winner via radio. The picked
 * factory persists to order.pickedQuoteFactoryId.
 */
import React from 'react';
import { Check, X, Award, DollarSign } from 'lucide-react';
import { useManifest } from '../ManifestContext';

const QuoteComparison = ({ order }) => {
  const { updateOrderFields } = useManifest();
  const quotes = order.quotes || [];
  const factoryById = (order.factories || []).reduce((m, f) => { m[f.id] = f; return m; }, {});

  const handlePick = (factoryId) => {
    updateOrderFields({ pickedQuoteFactoryId: factoryId });
  };

  // Compute cheapest landed cost for the "best price" tag
  const cheapestId = quotes.reduce((acc, q) =>
    !acc || q.landedCostCadEach < acc.landedCostCadEach ? q : acc, null,
  )?.factoryId;

  return (
    <div className="manifest-rich-panel manifest-quotes">
      <div className="manifest-rich-panel-head">
        <div className="manifest-rich-panel-title">
          <DollarSign size={16} /> Quote comparison
        </div>
        <div className="manifest-rich-panel-subtle">
          {quotes.length} of {order.factories?.length} factories returned quotes
        </div>
      </div>

      <div className="manifest-quote-grid">
        {quotes.map((q) => {
          const f = factoryById[q.factoryId];
          if (!f) return null;
          const certs = Object.entries(q.certMatch || {});
          const certsPassed = certs.filter(([, v]) => v).length;
          const certsTotal = certs.length;
          const allCertsMatch = certsPassed === certsTotal;
          const isPicked = order.pickedQuoteFactoryId === q.factoryId;
          const isCheapest = cheapestId === q.factoryId;

          return (
            <div
              key={q.factoryId}
              className={`manifest-quote-card ${isPicked ? 'picked' : ''} ${!allCertsMatch ? 'cert-warn' : ''}`}
            >
              {isPicked && (
                <div className="manifest-quote-flag picked">
                  <Award size={12} /> Selected
                </div>
              )}
              {!isPicked && isCheapest && (
                <div className="manifest-quote-flag cheapest">Lowest landed cost</div>
              )}
              <div className="manifest-quote-name">{f.name}</div>
              <div className="manifest-quote-country">{f.country}</div>

              <div className="manifest-quote-numbers">
                <div className="manifest-quote-num">
                  <span className="manifest-quote-num-label">Unit price</span>
                  <span className="manifest-quote-num-val">${q.unitPriceUsd} USD</span>
                </div>
                <div className="manifest-quote-num">
                  <span className="manifest-quote-num-label">Landed CAD</span>
                  <span className="manifest-quote-num-val">${q.landedCostCadEach} ea</span>
                </div>
                <div className="manifest-quote-num">
                  <span className="manifest-quote-num-label">Lead time</span>
                  <span className="manifest-quote-num-val">{q.leadTimeDays}d</span>
                </div>
                <div className="manifest-quote-num">
                  <span className="manifest-quote-num-label">Incoterm</span>
                  <span className="manifest-quote-num-val">{q.incoterm}</span>
                </div>
              </div>

              <div className="manifest-quote-certs">
                {certs.map(([k, v]) => (
                  <span key={k} className={`manifest-cert-pill ${v ? 'pass' : 'fail'}`}>
                    {v ? <Check size={11} /> : <X size={11} />} {k.replace(/_/g, '-')}
                  </span>
                ))}
              </div>

              {q.notes && <div className="manifest-quote-notes">{q.notes}</div>}

              <button
                type="button"
                className={`manifest-quote-pick ${isPicked ? 'picked' : ''}`}
                onClick={() => handlePick(q.factoryId)}
              >
                {isPicked ? '✓ Selected' : 'Pick this quote'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuoteComparison;
